import React, { useCallback, useContext } from 'react';

import { Ads } from '../../ads';
import HighlightedSearchResult from '../highlighted-search-result';
import { DealsContext } from '../deals-context';

import { isRaw } from '../../../utils/products';

import type { ActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';
import type { Product, Raw, Offer } from '../../../../../types';
import type { ProductComparisonProps } from '../types';
import type { AdsLayoutConfig } from '../../ads/types';
import { LayoutMode } from 'src/components/layout-buttons/types';

import * as S from './styles';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

type ProductCardWrapperProps = {
  onClick?: (productData: {
    id: string | number;
    name?: string;
    type?: string;
  }) => void;
  order: number;
  product: Product | Raw | Offer;
  productsWithActiveCampaign?: ActiveCampaignByProductId;
} & ProductComparisonProps;

const ProductCardWrapper = (props: ProductCardWrapperProps) => {
  const {
    deselectProductById,
    isSelectingProducts,
    onClick,
    order,
    product,
    productsWithActiveCampaign,
    selectedProducts,
    selectProductById,
  } = props;

  const { getDealDiffPercentage } = useContext(DealsContext);

  let id!: number;

  if (!isRaw(product)) {
    id = product.id;
  }

  const dealsDiffPercentage = productsWithActiveCampaign
    ? productsWithActiveCampaign[id]?.diffPercentage
    : getDealDiffPercentage(id);

  const onSelect = (productId: number) =>
    selectProductById && selectProductById(productId, 'productCard');

  const onDeselect = (productId: number) =>
    deselectProductById && deselectProductById(productId, 'productCard');

  const onClickHandler = useCallback(
    () =>
      onClick && onClick({ id, name: product.name, type: product.__typename }),
    [id]
  );

  const getPrice = () => {
    switch (product.__typename) {
      case 'Product':
        return product.priceSummary.regular;
      case 'Offer':
        return product.offerPrice.regular;
      case 'Raw':
        return product.price;
      default:
        return undefined;
    }
  };

  const disableUnfeaturedClickout = isRaw(product) && !product.store?.featured;

  return !isRaw(product) ? (
    <S.ProductCard
      diffPercentage={dealsDiffPercentage}
      fallbackImageSrc={product.category?.logo}
      highlightedName={
        product.highlight ? (
          <HighlightedSearchResult>{product.highlight}</HighlightedSearchResult>
        ) : undefined
      }
      id={product.id}
      imageSrc={product.media.first}
      link={product.pathName}
      name={product.name}
      price={product.priceSummary.regular}
      priceInclShipping={product.priceSummary.includeShipping}
      onClick={onClickHandler}
      onDeselect={() => onDeselect(product.id)}
      onSelect={() => onSelect(product.id)}
      $order={order}
      rating={
        product.aggregatedRating?.score || product.userReviewSummary?.rating
      }
      reviewsCount={
        product.aggregatedRating?.count || product.userReviewSummary?.count
      }
      selectable={Boolean(isSelectingProducts)}
      selected={Boolean(selectedProducts?.find(p => p.id === product.id))}
    />
  ) : (
    <S.OfferCard
      type="offer"
      key={`${product.name}-${order}`}
      name={product.name}
      onClick={onClickHandler}
      price={getPrice()}
      store={product.store}
      link={disableUnfeaturedClickout ? undefined : product.externalUri}
      $order={order}
      imageSrc={
        product.__typename === 'Offer' ? product.media?.first : undefined
      }
    />
  );
};

type Props = {
  feedbackPrompt?: React.ReactNode | null;
  feedbackPromptPosition?: number;
  onClick?: (productId: string | number, name?: string, type?: string) => void;
  products: (Product | Raw)[];
  productsWithActiveCampaign?: ActiveCampaignByProductId;
  adslayoutConfig?: AdsLayoutConfig;
  hasFeedback?: boolean;
} & ProductComparisonProps;

export const CompactListLayout = (props: Props) => {
  const {
    feedbackPrompt,
    feedbackPromptPosition = 5,
    adslayoutConfig,
    onClick,
    products,
    hasFeedback,
    ...productComparisonProps
  } = props;

  return (
    <S.ListUl data-test="ProductLayoutList">
      {products.map((product, index) => (
        <ProductCardWrapper
          key={
            !isRaw(product) ? product.id : `${product.name}-${index.toString()}`
          }
          onClick={(productData: {
            id: string | number;
            name?: string;
            type?: string;
          }) => {
            onClick?.(productData.id, productData.name, productData.type);
            if (!isRaw(product))
              trackInternalNavigationClick({
                scope: InternalNavigationScope.COMPACT_LIST_LAYOUT_PRODUCT_CARD,
                nextPage: product.pathName,
                index,
              });
          }}
          order={index + 1}
          product={product}
          {...productComparisonProps}
        />
      ))}

      <Ads
        offersLength={products.length}
        layout={LayoutMode.COMPACT_LIST}
        layoutConfig={adslayoutConfig}
      />

      {hasFeedback && (
        <li style={{ order: feedbackPromptPosition - 1 }}>{feedbackPrompt}</li>
      )}
    </S.ListUl>
  );
};
